<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-2.5">
        <h1 class="text-[26px] font-bold text-ink tracking-[-0.03em] m-0">Contacts</h1>
        <span v-if="contacts" class="bg-[#f0eef5] text-primary text-[13px] font-semibold px-2.5 py-0.5 rounded-full">{{ contacts.length }}</span>
      </div>
      <a href="#" @click.prevent="openEditModal(false)" class="bg-ink text-white text-sm font-medium px-6 py-2.5 rounded-full no-underline transition-opacity duration-150 hover:opacity-85 hover:text-white hover:no-underline">
        + Add contact
      </a>
    </div>

    <loader v-if="isLoading"></loader>

    <div v-if="!isLoading && (!contacts || contacts.length === 0)" class="flex flex-col items-center py-[60px] px-5 text-muted">
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
      <p class="text-sm text-muted mt-4 mb-0">No contacts yet</p>
      <a href="#" @click.prevent="openEditModal(false)" class="text-sm font-semibold text-primary no-underline mt-2 hover:text-[#6347c5] hover:no-underline">+ Add your first contact</a>
    </div>

    <div v-if="contacts && contacts.length > 0" class="flex flex-col gap-2">
      <div
        v-for="contact in contacts"
        :key="contact.id"
        class="bg-white border border-black/6 rounded-[20px] shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-[border-color,box-shadow] duration-150 hover:border-black/8 hover:shadow-[0_4px_12px_rgba(0,0,0,0.07)]"
        :class="{ 'opacity-45': contact.isActive === false }">
        <a href="#" @click.prevent="toggleContact(contact.id)" class="flex items-center justify-between px-5 py-4 no-underline text-inherit cursor-pointer hover:no-underline hover:text-inherit">
          <div class="flex items-center gap-3.5 min-w-0">
            <img v-if="contact.picture" class="size-11 rounded-full object-cover shrink-0" :src="'/static/images/clients/' + contact.picture" alt="" width="44">
            <div v-else class="size-11 rounded-full bg-[#f0eef5] text-primary text-sm font-semibold flex items-center justify-center shrink-0">{{ contactInitials(contact) }}</div>
            <div class="flex flex-col min-w-0">
              <span class="text-[15px] font-semibold text-ink leading-[1.3]">{{ contactName(contact) }}</span>
              <span v-if="contact.shortDescription" class="text-[13px] text-muted mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis">{{ contact.shortDescription }}</span>
            </div>
          </div>

          <div class="flex items-center gap-4 shrink-0">
            <div class="flex gap-2 max-md:hidden">
              <span v-if="contact.isActive" class="text-xs font-medium px-2.5 py-[3px] rounded-full whitespace-nowrap bg-[#e2f3d1] text-[#2d7a0e]">Active</span>
              <span v-else class="text-xs font-medium px-2.5 py-[3px] rounded-full whitespace-nowrap bg-[#fef2f2] text-[#e53935]">Inactive</span>
              <span v-if="contact.type" class="text-xs font-medium px-2.5 py-[3px] rounded-full whitespace-nowrap bg-[#f0eef5] text-primary capitalize">{{ contact.type }}</span>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="text-muted transition-transform duration-200"
              :class="{ 'rotate-90': expandedContact === contact.id }"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </div>
        </a>

        <div v-if="expandedContact === contact.id" class="mx-5 px-0 pb-4 pt-4 border-t border-black/6">
          <div v-if="contact.description" class="flex flex-col gap-0.5 mb-3">
            <span class="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">Description</span>
            <span class="text-sm text-ink leading-normal">{{ contact.description }}</span>
          </div>
          <div v-if="contact.endorsingBody" class="flex flex-col gap-0.5 mb-3">
            <span class="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">Endorsing body</span>
            <span class="text-sm text-ink leading-normal">{{ contact.endorsingBody }}</span>
          </div>
          <div class="flex gap-3 mt-4">
            <a href="#" @click.prevent="openEditModalWith(contact)" class="text-[13px] font-medium text-primary no-underline hover:underline hover:text-primary">Edit</a>
          </div>
        </div>
      </div>
    </div>

    <contact-add ref="modal" :item="editContact" @update="update"></contact-add>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ContactAdd from './ContactAddModal'

export default {
  name: 'contacts',
  components: {
    contactAdd: ContactAdd
  },
  data () {
    return {
      editContact: null,
      isLoading: false,
      expandedContact: null
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      contacts: state => state.contacts.contacts
    })
  },
  methods: {
    contactName (contact) {
      return contact.firstName + ' ' + contact.lastName
    },
    contactInitials (contact) {
      return (contact.firstName ? contact.firstName[0] : '') + (contact.lastName ? contact.lastName[0] : '')
    },
    openEditModal () {
      this.editContact = null
      this.$refs.modal.showModal()
    },
    openEditModalWith (contact) {
      this.editContact = contact
      this.$refs.modal.showModal()
    },
    update () {
      this.$store.dispatch('GET_CONTACTS')
      this.$refs.modal.hideModal()
    },
    toggleContact (id) {
      if (this.expandedContact === id) {
        this.expandedContact = null
      } else {
        this.expandedContact = id
      }
    }
  },
  mounted () {
  },
  created () {
    if (this.user && !this.user.isAdmin) {
      this.$router.push({ name: 'admin-applicants' })
    } else {
      this.isLoading = true
      this.$store.dispatch('GET_USER')
      this.$store.dispatch('GET_CONTACTS').then(() => {
        this.isLoading = false
      })
      this.$store.dispatch('GET_BODIES')
    }
  }
}
</script>
